import type { SettingsNavGroup } from "@/components/templates";
import { PEOPLE_ENGAGEMENT_SETTINGS_PATH, PEOPLE_SETTINGS_PATH } from "@/components/community/peopleSubNav";
import { canAccessSettingsSection } from "./settings-section-access";

/**
 * What decides which settings pages a viewer can open. Each field is the gate on those pages'
 * routes today, or a page's own no-permission gate, so the nav never offers a page that would
 * send the viewer away or refuse them.
 */
export interface SettingsNavViewer {
  /** An org admin or a platform super-admin: what SettingsPage checks for the admin `/settings/*` sections. */
  isAdmin: boolean;
  /** An org admin of this organization: OrgAdminRoute, and the org-admin-only sections (voice profile). */
  isOrgAdmin: boolean;
  /** Can open the contributions module: the ModuleRouteGate on every `/finance/settings/*` route. */
  finance: boolean;
  /**
   * May read Accounts payable settings: the page's own gate (`canReadPayablesSettings`), which
   * shows anyone else its no-permission state rather than the setting.
   */
  payablesSettings: boolean;
  /**
   * May read Scan-to-email intake: the page's own gate (`canReadScanToEmail`), which shows anyone
   * else its no-permission state rather than the settings.
   */
  scanToEmail: boolean;
  /**
   * The accounting module isn't switched off. Accounting setup, contribution defaults and
   * scan-to-email intake sit behind TierRouteGate, which sends the viewer away only then; on a plan
   * below it they show the upgrade card, as they do today.
   */
  accounting: boolean;
  /** May run the one-time pre-go-live cleanup (useFinanceCleanupAccess), as today's Finance menu checks. */
  financeCleanup: boolean;
  /** Can open the HR module: the ModuleRouteGate around every `/hr/*` route. */
  hr: boolean;
}

/** The plan gates that decide whether a Finance settings page opens as a page or as an upgrade card. */
export type FinanceSettingsPlanGate = "accounting" | "approvalWorkflows";

/** Whether each plan gate is open for the viewer: the plan includes the feature and it is switched on. */
export type FinanceSettingsPlanAccess = Record<FinanceSettingsPlanGate, boolean>;

interface SettingsNavDestination {
  id: string;
  label: string;
  path: string;
  /** Whether the viewer can open the page. */
  visible: (viewer: SettingsNavViewer) => boolean;
  /**
   * The plan gate the page sits behind, if any. The nav keeps a page whose gate is closed (the upgrade
   * card is how a viewer finds out it exists), but `/finance/settings` does not land on one (#7786).
   */
  plan?: FinanceSettingsPlanGate;
}

interface SettingsNavDefinition {
  label: string;
  items: SettingsNavDestination[];
}

/** A `/settings/:section` page: SettingsPage's own access rule, unchanged. */
function settingsSection(id: string, label: string): SettingsNavDestination {
  return {
    id,
    label,
    path: `/settings/${id}`,
    visible: ({ isAdmin, isOrgAdmin }) => canAccessSettingsSection(id, { isAdmin, isOrgAdmin }),
  };
}

/** A `/finance/settings/:section` page: the contributions module, plus any gate of its own. */
function financeSettings(
  section: string,
  label: string,
  extra: (viewer: SettingsNavViewer) => boolean = () => true,
  plan?: FinanceSettingsPlanGate,
): SettingsNavDestination {
  return {
    id: `finance-${section}`,
    label,
    path: `/finance/settings/${section}`,
    visible: (viewer) => viewer.finance && extra(viewer),
    plan,
  };
}

/**
 * Every settings page that exists today, grouped as PAGE_TEMPLATES.md § T5 groups them
 * (frame 8d): org-wide setup, then each module's settings (MODULE_MAPS.md rule 4), then
 * administration. Pages the spec names but nobody has built yet are left out until they are.
 * Personal preferences are not here: they configure one person, not the organization.
 */
export const SETTINGS_NAV: readonly SettingsNavDefinition[] = [
  {
    label: "Organization",
    items: [
      settingsSection("organization", "Church profile"),
      settingsSection("locations", "Locations"),
      settingsSection("ministries", "Ministry areas"),
      settingsSection("voice", "Voice profile"),
      settingsSection("custom-fields", "Custom fields"),
      settingsSection("option-lists", "Option lists"),
      settingsSection("tags", "Tags"),
      settingsSection("widgets", "Widgets"),
    ],
  },
  {
    label: "Modules",
    items: [
      // Directory settings sits behind OrgAdminRoute; engagement scoring needs only a sign-in.
      { id: "people-directory", label: "Directory settings", path: PEOPLE_SETTINGS_PATH, visible: (viewer) => viewer.isOrgAdmin },
      { id: "people-engagement", label: "Engagement scoring", path: PEOPLE_ENGAGEMENT_SETTINGS_PATH, visible: () => true },
      financeSettings("accounting-setup", "Accounting setup", (viewer) => viewer.accounting, "accounting"),
      financeSettings("contribution-defaults", "Contribution defaults", (viewer) => viewer.accounting, "accounting"),
      // Behind the approval_workflows feature (ApprovalPolicies renders an upgrade card below its plan).
      financeSettings("approval-policies", "Approval policies", undefined, "approvalWorkflows"),
      financeSettings("payables-settings", "Accounts payable", (viewer) => viewer.payablesSettings),
      // Behind the accounting module, as its route is (TierRouteGate), and the page's own read gate.
      financeSettings("ap-inbound-email", "Scan-to-email intake", (viewer) => viewer.accounting && viewer.scanToEmail, "accounting"),
      financeSettings("recurring-rules", "Recurring rules"),
      financeSettings("allocations", "Expense allocations"),
      financeSettings("bank-connections", "Bank connections"),
      financeSettings("integrations", "Finance integrations"),
      financeSettings("pre-go-live-cleanup", "Pre-go-live cleanup", (viewer) => viewer.financeCleanup),
      settingsSection("kids-checkin", "Children's check-in"),
      { id: "hr", label: "HR settings", path: "/hr/settings", visible: (viewer) => viewer.hr },
    ],
  },
  {
    label: "Administration",
    items: [
      settingsSection("team", "Team"),
      settingsSection("permissions", "Permissions"),
      settingsSection("security", "Security"),
      settingsSection("audit", "Audit log"),
      // Its own route, behind OrgAdminRoute (not SettingsPage's admin rule).
      { id: "integrations", label: "Integrations", path: "/settings/integrations", visible: (viewer) => viewer.isOrgAdmin },
      settingsSection("billing", "Subscription"),
      settingsSection("modules", "Enabled modules"),
      settingsSection("workflows", "Workflows"),
      settingsSection("today", "Today queue"),
      settingsSection("import", "Import data"),
    ],
  },
];

/**
 * Keeps a `?ui=` preview choice on the nav's links: where storage can't hold it, the URL is all
 * that does, and dropping it would switch the next page back to the other layout.
 */
export function settingsNavSearch(search: string): string {
  const ui = new URLSearchParams(search).get("ui");
  return ui === "next" || ui === "classic" ? `?ui=${ui}` : "";
}

/**
 * The nav for one viewer: each group keeps only the pages they can open, and a group left with
 * none is dropped. `search` is appended to every link (see `settingsNavSearch`).
 */
export function settingsNavGroups(viewer: SettingsNavViewer, search = ""): SettingsNavGroup[] {
  return SETTINGS_NAV.map((group) => ({
    label: group.label,
    items: group.items
      .filter((item) => item.visible(viewer))
      .map((item) => ({ id: item.id, label: item.label, href: `${item.path}${search}` })),
  })).filter((group) => group.items.length > 0);
}

/**
 * Where the bare `/finance/settings` goes (#7786): the first Finance settings page this viewer can
 * actually use, read from the same nav definition so the two cannot drift apart.
 *
 * "Use" means the page opens as a page. A page behind a plan gate that is closed for this viewer
 * (`access`) is skipped: with accounting switched off its routes send the viewer Home, and below the
 * plan they show an upgrade card, as Approval policies does without the approval_workflows feature.
 * Those pages stay in the nav (a below-plan viewer can still open one and see the upsell); they are
 * just not where "Settings" drops you. With every gate open the first page is Accounting setup, as
 * the old redirect was. Null when the viewer has no Finance settings page at all.
 */
export function financeSettingsLandingHref(
  viewer: SettingsNavViewer,
  access: FinanceSettingsPlanAccess,
  search = "",
): string | null {
  for (const group of SETTINGS_NAV) {
    for (const item of group.items) {
      if (!item.path.startsWith("/finance/settings/") || !item.visible(viewer)) continue;
      if (item.plan !== undefined && !access[item.plan]) continue;
      return `${item.path}${search}`;
    }
  }
  return null;
}

function normalizePath(path: string): string {
  const bare = path.split(/[?#]/)[0].toLowerCase();
  return bare.length > 1 ? bare.replace(/\/+$/, "") : bare;
}

/** The item whose page is `pathname` (router matching ignores case and a trailing slash), if the viewer has it. */
export function settingsNavActiveId(nav: SettingsNavGroup[], pathname: string): string | undefined {
  const current = normalizePath(pathname);
  for (const group of nav) {
    const item = group.items.find((candidate) => normalizePath(candidate.href) === current);
    if (item) return item.id;
  }
  return undefined;
}
